import { useCallback, useMemo } from 'react';
import { Variable } from 'src/api/types';
import { useAssessmentVariablesQuery } from 'src/graphql/_generated';

type UseAssessmentVariables = (a: { assessmentId?: string }) => {
  assessmentVariables: Variable[] | null;
  isAssessmentVariablesLoading: boolean;
  refetch: () => Promise<Variable[] | null>;
};

const mapVariables = (
  gqlVariables?: Array<{
    id?: number | null;
    name?: string | null;
    value?: number | null;
    isInUse: Array<string>;
    currency: boolean;
    isGlobal?: boolean | null;
    alias?: string | null;
    workspaceVariable?: boolean | null;
  }>,
): Variable[] | null => {
  if (!gqlVariables) return null;
  return gqlVariables.map(
    (el) =>
      ({
        id: el.id!,
        name: el.name!,
        value: el.value!,
        currency: el.currency,
        isInUse: el.isInUse as string[],
        workspace_variable: !!el.workspaceVariable,
        alias: el.alias || null,
        isGlobal: !!el.isGlobal,
      }) satisfies Variable,
  );
};

const useAssessmentVariables: UseAssessmentVariables = ({ assessmentId }) => {
  const {
    data,
    loading,
    refetch: apolloRefetch,
  } = useAssessmentVariablesQuery({
    variables: { id: assessmentId! },
    skip: !assessmentId,
  });

  const assessmentVariables = useMemo(() => mapVariables(data?.assessment?.variables), [data]);

  const refetch = useCallback(async () => {
    const result = await apolloRefetch();
    return mapVariables(result.data?.assessment?.variables);
  }, [apolloRefetch]);

  return { assessmentVariables, isAssessmentVariablesLoading: loading, refetch };
};

export default useAssessmentVariables;
